import { useVideoCreatorStore } from '@src/stores-v2/VideoCreatorStoreContext';
import styled from 'styled-components';

import SpinningLoading from '../SpinningLoading';

import type { ElementState } from '../../renderer/ElementState';
import type { Story } from '../../types.ts/story';
import type { FC } from 'react';

interface ImagePresetProps {
  activeElement: ElementState;
  parentCompositionElement?: ElementState;
  url: string;
  story?: Story;
  onSourceChange?: (newUrl: string) => void;
  isImageControl?: boolean;
  isLogoControl?: boolean;
  isVideoControl?: boolean;
}

export const ImagePreset: FC<ImagePresetProps> = props => {
  const videoCreator = useVideoCreatorStore();

  // videoCreator.getImageElement doesn't check if the argument is an image element,
  // it just checks if it is a composition element and gets a child image. if it's not
  // a composition, it returns the element itself. THEREFORE, this works for video
  // elements too, which is why it's working in VideoControl. We should make
  // this cleaner later.
  //
  const currentElement = videoCreator.getImageElement(props.activeElement);
  const url = currentElement.source.source;

  const getDescription = () => {
    if (currentElement.source.type === 'image') {
      const photoAssets = [
        ...(videoCreator.story?.storyArtifacts || []),
        ...(videoCreator.story?.storyAssets || []),
        ...(videoCreator.story?.aiPhotos || []),
        ...(videoCreator.organization?.organizationArtifacts || []),
        ...(videoCreator.organization?.organizationLogos || []),
      ];
      return photoAssets.find(
        p =>
          p.responsiveImage?.srcSet === url ||
          p.responsiveImage?.src === url ||
          p.url === url,
      )?.title;
    }
    return '';
  };

  const title = props.isVideoControl
    ? 'Swap or search for a new video'
    : undefined;
  const origin =
    props.isImageControl || props.isLogoControl || props.isVideoControl
      ? 'composition-settings'
      : undefined;

  return (
    <>
      <Main>
        {videoCreator.savingStockPhoto && (
          <SpinningLoading text="Saving stock image" positionTop="20px" />
        )}
        <ImageWrapper>
          <ImageOverlay />
          {props.isVideoControl ? (
            <Video src={props.url} />
          ) : (
            <Image src={url} alt="image present" />
          )}
          <ReplaceButton
            onClick={() =>
              (videoCreator.openPhotoElementReplacementModal = {
                element: props.activeElement,
                parentCompositionElement: props.parentCompositionElement,
                onImageSourceChange: props.onSourceChange,
                isLogoControl: props.isLogoControl,
                isVideoControl: props.isVideoControl || false,
                title,
                origin,
              })
            }
          >
            Replace
          </ReplaceButton>
        </ImageWrapper>

        <Description>{getDescription()}</Description>
      </Main>
    </>
  );
};

const Main = styled.div`
  position: relative;
  margin: 15px 0;
  // height: 80px;
  // background-size: contain;
  // background-repeat: no-repeat;
  // background-position: center;
  border-radius: 5px;
  cursor: pointer;
  display: flex;
  gap: 10px;
  align-items: center;

  &:after {
    content: '';
    position: absolute;
    bottom: 0;
    left: 0;
    right: 0;
    height: 40px;
    background: linear-gradient(
      to bottom,
      rgba(0, 0, 0, 0),
      rgba(0, 0, 0, 50%)
    );
  }
`;

const Image = styled.img`
  width: 107px;
  height: 70px;
  border-radius: inherit;
  background-size: cover;
  object-fit: cover;
`;

const Video = styled.video`
  width: 107px;
  height: 70px;
  border-radius: inherit;
`;

const ImageWrapper = styled.div`
  width: 107px;
  height: 70px;
  border-radius: 8px;
  position: relative;
`;
const ImageOverlay = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  z-index: 0;
  background-color: rgba(0, 0, 0, 0.3);
`;

const ReplaceButton = styled.button`
  outline: none;
  border: 1px solid #f2d093;
  border-radius: 4px;
  width: 55px;
  height: 28px;
  position: absolute;
  color: #f2d093;
  font-weight: 500;
  font-size: 10px;
  line-height: 12.1px;
  text-align: center;
  background-color: transparent;
  top: 21px;
  left: 26px;
  z-index: 1;
  cursor: pointer;
`;

const Description = styled.span`
  font-color: #d7d7e1;
  font-weight: 300;
  font-size: 10px;
  line-height: 12px;
`;
